import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { X, ArrowRight, ArrowLeft, Sparkles, PartyPopper } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface TourStep {
  selector: string;
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    selector: '[data-tour="create-listing"]',
    title: 'Create Your First Listing',
    description: 'Start building a professional property listing with AI-powered descriptions and photo enhancement.',
    position: 'bottom',
  },
  {
    selector: '[data-tour="demo-videos"]',
    title: 'See How It Works',
    description: 'Watch quick demos showing how to create listings manually or by importing from any URL.',
    position: 'top',
  },
  {
    selector: '[data-tour="nav-features"]',
    title: 'Explore Features',
    description: 'Discover all the tools available — photo enhancement, virtual staging, AI descriptions, and more.',
    position: 'bottom',
  },
  {
    selector: '[data-tour="account-menu"]',
    title: 'Your Account',
    description: 'Access your profile, saved listings, and usage dashboard from here.',
    position: 'bottom',
  },
];

const STORAGE_KEY = 'listbright_tour_completed';

export function WelcomeTour() {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Check if tour should show — localStorage is a fast cache, DB is the source of truth
  useEffect(() => {
    if (!user) return;

    // Fast path: already completed locally
    const completedLocally = localStorage.getItem(STORAGE_KEY);
    if (completedLocally) return;

    // Check DB
    const checkDb = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('tour_completed')
        .eq('id', user.id)
        .single();

      if (data?.tour_completed) {
        // Sync localStorage so we skip the DB call next time
        localStorage.setItem(STORAGE_KEY, 'true');
        return;
      }

      // Not completed — show tour after a short delay
      const timer = setTimeout(() => setIsActive(true), 1500);
      return () => clearTimeout(timer);
    };

    checkDb();
  }, [user]);

  const updateTargetRect = useCallback(() => {
    if (!isActive) return;
    const step = TOUR_STEPS[currentStep];
    const el = document.querySelector(step.selector);
    if (el) {
      // Scroll element into view first, then measure after scroll settles
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      // Delay measurement to let scroll finish
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        setTargetRect(rect);
      }, 400);
    } else {
      setTargetRect(null);
    }
  }, [currentStep, isActive]);

  useEffect(() => {
    updateTargetRect();
    window.addEventListener('resize', updateTargetRect);
    window.addEventListener('scroll', updateTargetRect, true);
    return () => {
      window.removeEventListener('resize', updateTargetRect);
      window.removeEventListener('scroll', updateTargetRect, true);
    };
  }, [updateTargetRect]);

  const completeTour = useCallback(async () => {
    setIsActive(false);
    localStorage.setItem(STORAGE_KEY, 'true');
    if (user) {
      await supabase
        .from('profiles')
        .update({ tour_completed: true } as any)
        .eq('id', user.id);
    }
  }, [user]);

  const nextStep = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      completeTour();
    }
  }, [currentStep, completeTour]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  }, [currentStep]);

  if (!isActive) return null;

  const step = TOUR_STEPS[currentStep];
  const isLast = currentStep === TOUR_STEPS.length - 1;
  const padding = 8;

  // Calculate tooltip position
  let tooltipStyle: React.CSSProperties = {};
  if (targetRect) {
    const gap = 16;
    switch (step.position) {
      case 'bottom':
        tooltipStyle = {
          top: targetRect.bottom + gap,
          left: targetRect.left + targetRect.width / 2,
          transform: 'translateX(-50%)',
        };
        break;
      case 'top':
        tooltipStyle = {
          bottom: window.innerHeight - targetRect.top + gap,
          left: targetRect.left + targetRect.width / 2,
          transform: 'translateX(-50%)',
        };
        break;
      case 'left':
        tooltipStyle = {
          top: targetRect.top + targetRect.height / 2,
          right: window.innerWidth - targetRect.left + gap,
          transform: 'translateY(-50%)',
        };
        break;
      case 'right':
        tooltipStyle = {
          top: targetRect.top + targetRect.height / 2,
          left: targetRect.right + gap,
          transform: 'translateY(-50%)',
        };
        break;
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999]" onClick={completeTour}>
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="tour-spotlight">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - padding}
                y={targetRect.top - padding}
                width={targetRect.width + padding * 2}
                height={targetRect.height + padding * 2}
                rx="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#tour-spotlight)"
          style={{ pointerEvents: 'all' }}
        />
      </svg>

      {/* Spotlight ring */}
      {targetRect && (
        <div
          className="absolute border-2 border-primary rounded-xl pointer-events-none animate-pulse"
          style={{
            top: targetRect.top - padding,
            left: targetRect.left - padding,
            width: targetRect.width + padding * 2,
            height: targetRect.height + padding * 2,
          }}
        />
      )}

      {/* Tooltip */}
      {targetRect && (
        <div
          ref={tooltipRef}
          onClick={(e) => e.stopPropagation()}
          className="absolute z-[10000] w-[320px] bg-card border border-border rounded-2xl shadow-2xl p-5"
          style={tooltipStyle}
        >
          <button
            onClick={completeTour}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 mb-2">
            {isLast ? (
              <PartyPopper className="w-5 h-5 text-primary" />
            ) : (
              <Sparkles className="w-5 h-5 text-primary" />
            )}
            <h3 className="font-display font-semibold text-foreground text-base">
              {step.title}
            </h3>
          </div>

          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
            {step.description}
          </p>

          <div className="flex items-center justify-between">
            {/* Step dots */}
            <div className="flex gap-1.5">
              {TOUR_STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === currentStep ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                />
              ))}
            </div>

            {/* Nav buttons */}
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button variant="ghost" size="sm" onClick={prevStep}>
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <Button size="sm" onClick={nextStep}>
                {isLast ? 'Done' : 'Next'}
                {!isLast && <ArrowRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
